import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2, AlertCircle, Film, Image as ImageIcon, RotateCw, CalendarClock } from "lucide-react";
import { Progress } from "./ui/progress";
import { http } from "../lib/api";

const CHUNK = 8 * 1024 * 1024;
const VIDEO_EXT = /\.(mp4|m4v|mov|webm|mkv|avi|wmv|flv|3gp|mpe?g|ts)$/i;
const IMAGE_EXT = /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i;
const kindOf = (f) =>
  f.type.startsWith("video/") || VIDEO_EXT.test(f.name) ? "video" : f.type.startsWith("image/") || IMAGE_EXT.test(f.name) ? "photo" : null;
const fmtSize = (b) => (b >= 1073741824 ? `${(b / 1073741824).toFixed(2)} GB` : `${(b / 1048576).toFixed(1)} MB`);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const putChunk = async (uploadId, offset, blob, onProgress) => {
  for (let attempt = 1; ; attempt++) {
    try {
      const r = await http.put(`/admin/uploads/${uploadId}`, blob, {
        params: { offset },
        timeout: 0,
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (ev) => onProgress(ev.loaded),
      });
      return r.data?.received ?? offset + blob.size;
    } catch (err) {
      const st = err.response?.status;
      if (st === 401 || st === 403 || attempt >= 5) throw err;
      await sleep(1000 * attempt);
    }
  }
};

const uploadOne = async (item, meta, onProgress) => {
  const { file } = item;
  const init = await http.post("/admin/uploads", { filename: file.name, size: file.size, content_type: file.type });
  const id = init.data.upload_id;
  const chunk = init.data.chunk_size || CHUNK;
  let offset = 0;
  while (offset < file.size) {
    const blob = file.slice(offset, offset + chunk);
    const base = offset;
    offset = await putChunk(id, offset, blob, (loaded) => onProgress(Math.min(99, Math.round(((base + loaded) / file.size) * 100))));
  }
  const fd = new FormData();
  Object.entries({ ...meta, title: item.title || file.name, kind: item.kind }).forEach(([k, v]) => fd.append(k, v));
  await http.post(`/admin/uploads/${id}/complete`, fd, { timeout: 0 });
};

const errMsg = (err) => {
  const st = err.response?.status;
  if (st === 401 || st === 403) return "Sessão expirada. Entre novamente.";
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : st ? `Erro ${st}` : "Falha de conexão";
};

const inputCls = "w-full border border-slate-800 bg-[#0d0c11] px-4 py-3 text-white outline-none transition-colors focus:border-rose-500";

const QueueItem = ({ it, busy, onTitle, onRemove }) => (
  <div className="grid grid-cols-[28px_1fr_auto] items-center gap-3 border-b border-white/5 py-3" data-testid={`queue-item-${it.key}`}>
    {it.kind === "video" ? <Film size={18} className="text-rose-400" /> : <ImageIcon size={18} className="text-rose-400" />}
    <div className="min-w-0">
      <input value={it.title} disabled={busy || it.status === "done"} onChange={(e) => onTitle(e.target.value)} className="w-full bg-transparent text-sm text-white outline-none focus:text-rose-300" data-testid={`queue-title-${it.key}`} />
      <p className="font-mono-d mt-1 truncate text-xs text-slate-400">{it.file.name} · {fmtSize(it.file.size)}</p>
      {it.status === "uploading" && <Progress value={it.progress} className="mt-2 h-1" />}
      {it.status === "error" && <p className="mt-1 text-xs text-rose-300" data-testid={`queue-error-${it.key}`}>{it.error}</p>}
    </div>
    <div className="font-mono-d flex items-center gap-2 text-xs text-slate-300">
      {it.status === "uploading" && <span>{it.progress}%</span>}
      {it.status === "done" && <CheckCircle2 size={18} className="text-emerald-400" />}
      {it.status === "error" && <AlertCircle size={18} className="text-rose-400" />}
      {!busy && it.status !== "done" && (
        <button type="button" onClick={onRemove} className="p-1 text-slate-400 hover:text-white" aria-label="Remover" data-testid={`queue-remove-${it.key}`}><X size={16} /></button>
      )}
    </div>
  </div>
);

export const UploadQueue = ({ onDone }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ description: "", category: "", tags: "", featured: false });
  const [schedule, setSchedule] = useState({ on: false, at: "" });
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const patch = (key, p) => setItems((l) => l.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const add = (files) => {
    const list = Array.from(files || []);
    const bad = list.filter((f) => !kindOf(f));
    if (bad.length) toast.error(`${bad.length} arquivo(s) ignorado(s): formato não suportado`);
    const ok = list.filter(kindOf).map((f) => ({
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      kind: kindOf(f),
      title: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "),
      status: "pending",
      progress: 0,
      error: "",
    }));
    setItems((l) => [...l, ...ok]);
  };

  const start = async () => {
    const todo = items.filter((i) => i.status !== "done");
    if (!todo.length) return toast.error("Adicione arquivos primeiro");
    let publishAt = "";
    if (schedule.on) {
      const d = new Date(schedule.at);
      if (!schedule.at || Number.isNaN(d.getTime()) || d <= new Date()) return toast.error("Escolha uma data e hora no futuro");
      publishAt = d.toISOString();
    }
    setBusy(true);
    let ok = 0;
    for (const it of todo) {
      patch(it.key, { status: "uploading", progress: 0, error: "" });
      try {
        await uploadOne(it, { ...meta, publish_at: publishAt }, (p) => patch(it.key, { progress: p }));
        patch(it.key, { status: "done", progress: 100 });
        ok++;
      } catch (err) {
        patch(it.key, { status: "error", error: errMsg(err) });
      }
    }
    setBusy(false);
    if (ok) {
      toast.success(`${ok} arquivo(s) publicado(s)`);
      onDone?.();
    }
    if (ok < todo.length) toast.error(`${todo.length - ok} arquivo(s) com erro. Clique em "Tentar novamente".`);
  };

  const pending = items.filter((i) => i.status !== "done").length;
  const total = items.reduce((s, i) => s + i.file.size, 0);
  const set = (k) => (e) => setMeta({ ...meta, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="upload-form">
      <div>
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) add(e.dataTransfer.files); }}
          className={`flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-3 border border-dashed p-8 text-center transition-colors ${drag ? "border-rose-500 bg-rose-950/30" : "border-rose-900/60 bg-[#0d0c11] hover:border-rose-500"}`}
          data-testid="upload-dropzone"
        >
          <UploadCloud size={36} className="text-rose-500" />
          <span className="font-serif-d text-2xl text-white">Arraste ou selecione vários arquivos</span>
          <span className="text-sm text-slate-400">Vídeos de qualquer tamanho (MP4, MOV, WEBM, MKV...) e fotos (JPG, PNG, WEBP...)</span>
          <input ref={inputRef} type="file" multiple disabled={busy} accept="video/*,image/*,.mov,.mkv,.m4v,.avi,.wmv,.heic" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
        </label>
        {items.length > 0 && (
          <div className="mt-6" data-testid="upload-queue">
            <div className="mb-2 flex justify-between font-mono-d text-xs text-slate-400">
              <span>{items.length} arquivo(s) · {fmtSize(total)}</span>
              {!busy && <button type="button" onClick={() => setItems((l) => l.filter((i) => i.status !== "done"))} className="hover:text-white" data-testid="queue-clear-done">Limpar concluídos</button>}
            </div>
            {items.map((it) => (
              <QueueItem key={it.key} it={it} busy={busy} onTitle={(t) => patch(it.key, { title: t })} onRemove={() => setItems((l) => l.filter((i) => i.key !== it.key))} />
            ))}
          </div>
        )}
      </div>
      <div className="space-y-5">
        <p className="eyebrow !text-slate-400">Aplicado a todos os arquivos da fila</p>
        <textarea rows={3} value={meta.description} onChange={set("description")} placeholder="Descrição" className={inputCls} data-testid="upload-description-input" />
        <div className="grid gap-5 sm:grid-cols-2">
          <input value={meta.category} onChange={set("category")} placeholder="Categoria (ex: Ensaios)" className={inputCls} data-testid="upload-category-input" />
          <input value={meta.tags} onChange={set("tags")} placeholder="Tags: tag1, tag2" className={inputCls} data-testid="upload-tags-input" />
        </div>
        <label className="flex items-center gap-3 text-sm text-slate-300">
          <input type="checkbox" checked={meta.featured} onChange={set("featured")} className="h-4 w-4 accent-rose-600" data-testid="upload-featured-checkbox" /> Marcar como destaque
        </label>
        <div className="border border-slate-800 p-4" data-testid="upload-schedule">
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" checked={schedule.on} onChange={(e) => setSchedule({ ...schedule, on: e.target.checked })} className="h-4 w-4 accent-rose-600" data-testid="upload-schedule-checkbox" />
            <CalendarClock size={16} className="text-rose-400" /> Agendar publicação
          </label>
          {schedule.on && (
            <div className="mt-3">
              <input type="datetime-local" value={schedule.at} onChange={(e) => setSchedule({ ...schedule, at: e.target.value })} className={`${inputCls} [color-scheme:dark]`} data-testid="upload-schedule-input" />
              <p className="mt-2 text-xs text-slate-400">Os arquivos ficam ocultos para visitantes e aparecem sozinhos nessa data e hora (seu fuso horário).</p>
            </div>
          )}
        </div>
        <p className="text-xs leading-relaxed text-slate-400">A capa e a duração dos vídeos são geradas automaticamente. Arquivos grandes são enviados em partes de 8 MB e retomados se a conexão cair. Mantenha esta aba aberta até terminar.</p>
        <button type="button" onClick={start} disabled={busy || !pending} className="flex w-full items-center justify-center gap-2 bg-rose-600 px-6 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-rose-500 disabled:opacity-50" data-testid="upload-submit-button">
          {busy ? "Enviando..." : items.some((i) => i.status === "error") ? <><RotateCw size={15} /> Tentar novamente ({pending})</> : `Publicar ${pending || ""} arquivo(s)`}
        </button>
      </div>
    </div>
  );
};
